// MediaPipe公式サンプルを参考
// https://ai.google.dev/edge/mediapipe/solutions/vision/pose_landmarker

import {PoseLandmarker, HandLandmarker, FilesetResolver} from '@mediapipe/tasks-vision';

import {
  ETextParamType_IsAvailableWebCamTracking,
  ETextParamType_StopWebCamTracking,
  ETextParamType_OperateCanvas
} from './logics/const';
import {hel_webcamtracking_setposedata, hel_webcamtracking_sethanddata} from './logics/misc';
import {hel_setText} from './logics/moduleCaller';
import {hel_isMobile} from './logics/platform';
import {hel_onloaded_add} from './logics/shared';

import type {HandLandmarkerResult, PoseLandmarkerResult} from '@mediapipe/tasks-vision';

let g_WebCamTrackingOnLoaded: boolean = false;
let g_WebCamTrackingScriptOnLoaded: boolean = false;

let g_WebCamPoseLandmarker: PoseLandmarker | null = null;
let g_WebCamHandLandmarker: HandLandmarker | null = null;
let g_Pose_ComputeDevice: 'CPU' | 'GPU' | undefined = 'GPU';
let g_Hands_ComputeDevice: 'CPU' | 'GPU' | undefined = 'GPU';
let g_PoseModelType: 'lite' | 'medium' | 'heavy' = 'medium';
let g_CameraResolutions: 'low' | 'medium' | 'high' = 'medium';
let g_lastVideoTime = -1;

// 全身トラッキング
let LeftWristPos: any = null;
let RightWristPos: any = null;

// WebCam
let g_WebCamTrackingRun = false;
let g_webcam_videoElement: HTMLVideoElement | null = null;
let g_webcam_videoStream: MediaStream | null = null;

const createAPIScripts = async () => {
  g_WebCamTrackingScriptOnLoaded = true;

  // トラッキング開始
  await startWebCamTracking();
};

const removeScriptTag = (src: string) => {
  const scriptTagList = document.querySelectorAll(`script[src="${src}"]`);

  for (let i = 0; i < scriptTagList.length; i++) {
    const scriptTag = scriptTagList[i];
    if (scriptTag) {
      scriptTag.remove();
    }
  }
};

const OnLoadedForWebCamTracking = () => {
  g_WebCamTrackingOnLoaded = true;
};

hel_onloaded_add(OnLoadedForWebCamTracking);

const startWebCamTracking = async () => {
  await createPoseLandmarker();
  await createHandLandmarker();

  // Videoを動的に生成
  if (g_webcam_videoElement === null) {
    // bodyに追加しないので画面には表示されない
    g_webcam_videoElement = document.createElement('video');

    g_webcam_videoElement.className = 'webcam_video';

    // 念のためCSSでも非表示状態にする
    // サイズを0にして見えなくする(カメラには1280x720が指定されているので認識はされる)
    g_webcam_videoElement.width = 0;
    g_webcam_videoElement.height = 0;
    g_webcam_videoElement.style.visibility = 'hidden';
  }

  await startCamera();

  g_WebCamTrackingRun = true;
};

const startCamera = async () => {
  const constraints = {video: true};

  const hasUserMedia = () => navigator.mediaDevices?.getUserMedia;
  if (hasUserMedia()) {
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        const track = stream.getVideoTracks()[0];
        if (!track) {
          throw new Error(
            '(await navigator.mediaDevices.getUserMedia()).getVideoTracks() returns an empty array.'
          );
        }

        // const capabilities = track.getCapabilities(); // カメラの能力を取得

        let width = 1280;
        let height = 720;

        switch (g_CameraResolutions) {
          case 'low': {
            width = 640;
            height = 480;
            break;
          }

          case 'medium': {
            width = 1280;
            height = 720;
            break;
          }

          case 'high': {
            width = 1920;
            height = 1080;
            break;
          }

          default: {
            width = 1280;
            height = 720;
            break;
          }
        }

        // カメラ解像度を再設定
        track
          .applyConstraints({
            width: width,
            height: height
          })
          .then(() => {
            if (g_webcam_videoElement) {
              g_webcam_videoStream = stream;

              g_webcam_videoElement.srcObject = stream;
              g_webcam_videoElement.addEventListener('loadeddata', predictWebCam);
              g_webcam_videoElement.play();
            }
          });
      })
      .catch((error) => {
        console.error('カメラの起動に失敗しました: ', error);

        // カメラが使えないのでモーションキャプチャを終了する
        forceStopWebCamTracking();
      });
  } else {
    const msg = 'navigator.mediaDevices.getUserMedia is not working.';
    console.error('カメラの起動に失敗しました: ', msg);
    forceStopWebCamTracking();
  }
};

const stopCamera = () => {
  closePoseLandmarker();
  closeHandLandmarker();

  if (g_webcam_videoElement) {
    g_webcam_videoElement.pause();
    g_webcam_videoElement.srcObject = null;
  }

  if (g_webcam_videoStream) {
    g_webcam_videoStream.getTracks().forEach((track) => track.stop());
    g_webcam_videoStream = null;
  }
};

const forceStopWebCamTracking = () => {
  // モーションキャプチャを強制終了。使えないようにする
  hel_setText(ETextParamType_StopWebCamTracking, '');
  hel_setText(
    ETextParamType_OperateCanvas,
    'ToggleDefault,,SettingSystem_WebCamTracking_Enable_Disable'
  );
  hel_setText(
    ETextParamType_OperateCanvas,
    'ToggleDefaultChange,,SettingSystem_WebCamTracking_Available'
  );
};

const createPoseLandmarker = async (): Promise<void> => {
  closePoseLandmarker();

  const vision = await FilesetResolver.forVisionTasks(
    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
  );

  let modelAssetPath = '';

  switch (g_PoseModelType) {
    case 'lite':
      modelAssetPath =
        'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task';
      break;

    case 'medium':
      modelAssetPath =
        'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task';
      break;

    case 'heavy':
      modelAssetPath =
        'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/latest/pose_landmarker_heavy.task';
      break;

    default:
      modelAssetPath =
        'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task';
      break;
  }

  g_WebCamPoseLandmarker = await PoseLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: modelAssetPath,
      delegate: g_Pose_ComputeDevice
    },
    runningMode: 'VIDEO',
    numPoses: 1,
    minPoseDetectionConfidence: 0.5,
    minPosePresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
    outputSegmentationMasks: false
  });
};

const closePoseLandmarker = () => {
  if (g_WebCamPoseLandmarker) {
    g_WebCamPoseLandmarker.close();
    g_WebCamPoseLandmarker = null;
  }
};

const createHandLandmarker = async (): Promise<void> => {
  closeHandLandmarker();

  const vision = await FilesetResolver.forVisionTasks(
    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
  );

  g_WebCamHandLandmarker = await HandLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: `https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/latest/hand_landmarker.task`,
      delegate: g_Hands_ComputeDevice
    },
    runningMode: 'VIDEO',
    numHands: 2
  });
};

const closeHandLandmarker = () => {
  if (g_WebCamHandLandmarker) {
    g_WebCamHandLandmarker.close();
    g_WebCamHandLandmarker = null;
  }
};

// let g_CurrentPredictTime: number = 0;

const predictWebCam = () => {
  if (!g_WebCamTrackingRun) return;

  if (!g_webcam_videoElement || !g_webcam_videoStream) return;

  if (g_lastVideoTime !== g_webcam_videoElement.currentTime) {
    g_lastVideoTime = g_webcam_videoElement.currentTime;

    const startTimeMs = performance.now();

    // let lastPredictTime = g_CurrentPredictTime;
    // g_CurrentPredictTime = startTimeMs / 1000.0;

    // let deltaTime = g_CurrentPredictTime - lastPredictTime;
    // let FPS = 1.0 / deltaTime;
    // console.debug("[JS] FPS: %f", FPS);

    g_WebCamPoseLandmarker?.detectForVideo(g_webcam_videoElement, startTimeMs, (results) => {
      onPoseResults(results);
    });

    const handResults = g_WebCamHandLandmarker?.detectForVideo(g_webcam_videoElement, startTimeMs);
    onHandsResults(handResults);
  }

  requestAnimationFrame(predictWebCam);
};

const onPoseResults = (results: PoseLandmarkerResult) => {
  if (results === undefined || results === null) return;

  // console.debug("[Pose] %o", results);

  if (!g_WebCamTrackingOnLoaded) return;

  const landmarks = results.landmarks;
  const worldLandmarks = results.worldLandmarks;
  if (
    landmarks === undefined ||
    landmarks.length === 0 ||
    worldLandmarks === undefined ||
    worldLandmarks.length === 0
  ) {
    return;
  }

  const poseLandmarks = landmarks[0];
  const poseWorldLandmarks = worldLandmarks[0];

  if (
    poseLandmarks === undefined ||
    poseLandmarks.length !== 33 ||
    poseWorldLandmarks === undefined ||
    poseWorldLandmarks.length !== 33
  ) {
    return;
  }

  let Text = '0'; // バージョン

  // ３Ｄ座標系
  for (const [n, Landmark] of poseWorldLandmarks.entries()) {
    const X = -Landmark.x;
    const Y = -Landmark.y + 0.75;
    const Z = -Landmark.z;
    const V = Landmark.visibility;

    if (n === 15) {
      LeftWristPos = Landmark;
      LeftWristPos.x = X;
      LeftWristPos.y = Y;
      LeftWristPos.z = Z;
    } else if (n === 16) {
      RightWristPos = Landmark;
      RightWristPos.x = X;
      RightWristPos.y = Y;
      RightWristPos.z = Z;
    }

    Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z) + ',' + String(V);
  }

  // スクリーン座標系
  for (const Landmark of poseLandmarks) {
    const X = Landmark.x;
    const Y = Landmark.y;
    const Z = Landmark.z;
    const V = Landmark.visibility;

    Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z) + ',' + String(V);
  }

  //
  hel_webcamtracking_setposedata(Text);
};

// ハンドトラッキング
const onHandsResults = (results: HandLandmarkerResult | undefined) => {
  if (results === undefined || results === null) return;

  // console.debug("[Hands] %o", results);

  if (!g_WebCamTrackingOnLoaded) return;

  const landmarks = results.landmarks;
  const worldLandmarks = results.worldLandmarks;
  const handednesses = results.handednesses;
  if (
    landmarks === undefined ||
    landmarks.length === 0 ||
    worldLandmarks === undefined ||
    worldLandmarks.length === 0 ||
    handednesses === undefined ||
    handednesses.length === 0
  ) {
    return;
  }

  let Text = '0'; // バージョン

  // ワールド座標
  Text += ',' + String(handednesses.length); // 手の個数
  for (let i = 0; i < handednesses.length; i++) {
    const PosList = worldLandmarks[i];
    if (!PosList?.[0] || !g_WebCamTrackingOnLoaded) {
      continue;
    }

    const handednessList = handednesses[i];
    if (!handednessList?.[0]) {
      continue;
    }

    const multiHandedness = handednessList[0];

    const Left = multiHandedness.categoryName === 'Left';

    Text += ',' + (Left ? '0' : '1'); // 左右フラグ

    const WristX = PosList[0].x;
    const WristY = PosList[0].y;
    const WristZ = PosList[0].z;

    for (const Pos of PosList) {
      let X = Pos.x;
      let Y = Pos.y;
      let Z = Pos.z;

      X -= WristX;
      Y -= WristY;
      Z -= WristZ;

      X = -X;
      Y = -Y;
      Z = -Z;

      if (!Left && LeftWristPos != null) {
        X = X + LeftWristPos.x;
        Y = Y + LeftWristPos.y;
        Z = Z + LeftWristPos.z;
      } else if (Left && RightWristPos != null) {
        X = X + RightWristPos.x;
        Y = Y + RightWristPos.y;
        Z = Z + RightWristPos.z;
      } else {
        X = X + WristX;
        Y = Y + WristY;
        Z = Z + WristZ;
      }

      Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z);
    }
  }

  // スクリーン座標
  Text += ',' + String(handednesses.length); // 手の個数
  for (let i = 0; i < handednesses.length; i++) {
    const PosList = landmarks[i];
    if (!PosList) {
      continue;
    }

    const handednessList = handednesses[i];
    if (!handednessList?.[0]) {
      continue;
    }

    const multiHandedness = handednessList[0];

    const Left = multiHandedness.categoryName === 'Left';

    Text += ',' + (Left ? '0' : '1'); // 左右フラグ

    for (const Pos of PosList) {
      const X = Pos.x;
      const Y = Pos.y;
      const Z = Pos.z;

      Text += ',' + String(X) + ',' + String(Y) + ',' + String(Z);
    }
  }

  // データ送信
  hel_webcamtracking_sethanddata(Text);
};

Object.assign(globalThis, {
  hel_webcamtracking_start: () => {
    if (hel_setText(ETextParamType_IsAvailableWebCamTracking, '') === 0) return;

    // スマホでは重いので非対応とする
    if (hel_isMobile()) return;

    if (g_WebCamTrackingRun) return;

    if (!g_WebCamTrackingScriptOnLoaded) {
      // scriptタグを作成
      createAPIScripts();
    } else {
      startWebCamTracking();
    }
  },

  hel_webcamtracking_stop: () => {
    if (hel_setText(ETextParamType_IsAvailableWebCamTracking, '') === 0) return;

    // スマホでは重いので非対応とする
    if (hel_isMobile()) return;

    if (!g_WebCamTrackingRun) return;

    // Cameraとhands/poseを両方とも停止するとmediaposeのwasm内でエラーが出る
    // Camera.stopを呼べばonPoseResultsやOnHandsResultsのコールバックは呼ばれなくなるので実質停止扱いになる
    // なので一度MediaPipeのリソースを作成したら、UIのEnable/DisableではCameraのstart/stopを切り替えるだけにする
    stopCamera();
    // webcamtracking_hands_stop();
    // webcamtracking_pose_stop();

    g_WebCamTrackingRun = false;
  },

  hel_webcamtracking_set_pose_computedevice: (computeDevice: 'CPU' | 'GPU' | undefined) => {
    // モーションキャプチャオフでも設定だけは反映されるようにする
    g_Pose_ComputeDevice = computeDevice;

    if (!g_WebCamTrackingRun) return;

    // カメラを再起動してLandmarkerを作りなおす
    stopCamera();
    startWebCamTracking();
  },

  hel_webcamtracking_get_pose_computedevice: (): 'CPU' | 'GPU' | undefined => {
    return g_Pose_ComputeDevice;
  },

  hel_webcamtracking_set_hands_computedevice: (computeDevice: 'CPU' | 'GPU' | undefined) => {
    // モーションキャプチャオフでも設定だけは反映されるようにする
    g_Hands_ComputeDevice = computeDevice;

    if (!g_WebCamTrackingRun) return;

    // カメラを再起動してLandmarkerを作りなおす
    stopCamera();
    startWebCamTracking();
  },

  hel_webcamtracking_get_hands_computedevice: (): 'CPU' | 'GPU' | undefined => {
    return g_Hands_ComputeDevice;
  },

  hel_webcamtracking_set_pose_modeltype: (modelType: 'lite' | 'medium' | 'heavy') => {
    // モーションキャプチャオフでも設定だけは反映されるようにする
    g_PoseModelType = modelType;

    if (!g_WebCamTrackingRun) return;

    // カメラを再起動してLandmarkerを作りなおす
    stopCamera();
    startWebCamTracking();
  },

  hel_webcamtracking_get_pose_modeltype: (): 'lite' | 'medium' | 'heavy' => {
    return g_PoseModelType;
  },

  hel_webcamtracking_set_camera_resolutions: (resolutions: 'low' | 'medium' | 'high') => {
    // モーションキャプチャオフでも設定だけは反映されるようにする
    g_CameraResolutions = resolutions;

    if (!g_WebCamTrackingRun) return;

    // カメラを再起動してLandmarkerを作りなおす
    stopCamera();
    startWebCamTracking();
  },

  hel_webcamtracking_get_camera_resolutions: (): 'low' | 'medium' | 'high' => {
    return g_CameraResolutions;
  }
});
